import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical, Pencil, Archive, ArchiveRestore, Trash2 } from 'lucide-react';

const BoardActionsMenu = ({ board, onRename, onArchive, onUnarchive, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(board.name);
  const menuRef = useRef(null);
  const renameRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (renaming) {
      setRenameValue(board.name);
      setTimeout(() => renameRef.current && renameRef.current.focus(), 50);
    }
  }, [renaming, board.name]);

  const handleRenameConfirm = () => {
    const name = renameValue.trim();
    if (name && name !== board.name) {
      onRename(name);
    }
    setRenaming(false);
    setOpen(false);
  };

  const handleRenameKeyDown = (e) => {
    if (e.key === 'Enter') handleRenameConfirm();
    if (e.key === 'Escape') { setRenaming(false); setOpen(false); }
  };

  if (renaming) {
    return (
      <div className="flex items-center gap-1 ml-1">
        <input
          ref={renameRef}
          type="text"
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onKeyDown={handleRenameKeyDown}
          onBlur={handleRenameConfirm}
          className="px-2 py-1 text-sm border border-blue-400 rounded focus:outline-none w-28"
        />
      </div>
    );
  }

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-gray-200 text-gray-500 transition-opacity"
      >
        <MoreVertical size={13} />
      </button>

      {open && (
        <div className="absolute top-6 left-0 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[130px]">
          <button
            onClick={() => { setOpen(false); setRenaming(true); }}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Pencil size={13} />
            Rename
          </button>

          {!board.is_default && !board.is_archived && (
            <button
              onClick={() => { setOpen(false); onArchive(); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Archive size={13} />
              Archive
            </button>
          )}

          {board.is_archived && (
            <button
              onClick={() => { setOpen(false); onUnarchive(); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              <ArchiveRestore size={13} />
              Unarchive
            </button>
          )}

          {!board.is_default && !board.is_archived && (
            <button
              onClick={() => { setOpen(false); onDelete(); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
            >
              <Trash2 size={13} />
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BoardActionsMenu;
